import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useGetApiRequests } from "../../../common/CommonExport";
import { Designation, Employee, EmployeeListResponse, KpiState, DesignationListResponse, EmployeeKpiResponse, ApiError } from "../types";
import dayjs from "dayjs";
import { triggerNotification } from "../../../common/CommonExport";

export const useKpiData = () => {
  const [viewMode, setViewMode] = useState<"view" | "add">("view");
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<Employee[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewMonthYear, setViewMonthYear] = useState(dayjs().format("MM-YYYY"));
  const [addMonthYear, setAddMonthYear] = useState(dayjs().format("MM-YYYY"));
  const [designationList, setDesignationList] = useState<Designation[]>([]);
  const [employeeList, setEmployeeList] = useState<Employee[]>([]);
  const [selectedDesignationId, setSelectedDesignationId] = useState<number | undefined>(undefined);

  const monthYear = viewMode === "view" ? viewMonthYear : addMonthYear;

  const orgId = useSelector((state: KpiState) => state?.organisationId?.id);
  const reportingManagerId = useSelector((state: KpiState) => state?.userId?.id);

  const getEmployeeKpiList = useGetApiRequests("employeeKpiList", "GET");
  const getDesignationList = useGetApiRequests("designationListKpi", "GET");
  const getEmployeeListApi = useGetApiRequests("employeeListKpiAdd", "GET");

  const fetchDesignationList = async () => {
    if (!reportingManagerId) return;
    try {
      const queryParams = { reportingManagerId };
      const response = await getDesignationList(null, queryParams);
      if (response && "data" in response) {
        const apiResponse = response.data as DesignationListResponse;
        setDesignationList(apiResponse.data || []);
        if (apiResponse.data?.length > 0) {
          setSelectedDesignationId(apiResponse.data[0].designationId);
        }
      }
    } catch (error) {
      console.error("Failed to fetch designation list", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "Failed to fetch designation list.";
      triggerNotification("error", errorMessage, "", "topRight");
    }
  };

  const fetchEmployeeList = async (page: number, size: number) => {
    if (!orgId || !reportingManagerId || !selectedDesignationId) return;
    setLoading(true);
    try {
      const queryParams = {
        organisationId: orgId,
        designationId: selectedDesignationId,
        start: (page - 1) * size,
        limit: size,
        monthYear: addMonthYear,
      };
      const pathParams = { id: reportingManagerId };
      const response = await getEmployeeListApi(null, queryParams, pathParams);
      if (response && "data" in response) {
        const apiResponse = response.data as EmployeeListResponse;
        setEmployeeList(apiResponse.data || []);
        setTotalResults(apiResponse.totalResults || 0);
      }
    } catch (error) {
      console.error("Failed to fetch employee list", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "Failed to fetch employee list.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setLoading(false);
    }
  };

  const fetchKpiData = async (page: number, size: number) => {
    if (!orgId || !reportingManagerId || !selectedDesignationId) return;
    setLoading(true);
    try {
      const queryParams = {
        reportingMangerId: reportingManagerId,
        organisationId: orgId,
        designationId: selectedDesignationId,
        start: (page - 1) * size,
        limit: size,
      };
      const pathParams = { monthYear: viewMonthYear };
      const response = await getEmployeeKpiList(null, queryParams, pathParams);
      if (response && "data" in response) {
        const apiResponse = response.data as EmployeeKpiResponse;
        setKpiData(apiResponse.data || []);
        setTotalResults(apiResponse.totalResults || 0);
      }
    } catch (error) {
      console.error("Failed to fetch KPI data", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "Failed to fetch KPI data.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId && reportingManagerId) {
      fetchDesignationList();
    }
  }, [orgId, reportingManagerId]);

  useEffect(() => {
    if (viewMode === "view" && orgId && reportingManagerId && selectedDesignationId) {
      fetchKpiData(currentPage, pageSize);
    } else if (viewMode === "add" && orgId && reportingManagerId && selectedDesignationId) {
      fetchEmployeeList(currentPage, pageSize);
    }
  }, [currentPage, pageSize, viewMode, viewMonthYear, addMonthYear, orgId, reportingManagerId, selectedDesignationId]);

  const handleTabChange = (key: string) => {
    setViewMode(key as "view" | "add");
    setCurrentPage(1);
  };

  const onShowSizeChange = (current: number, size: number) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDesignationChange = (designationId: number) => {
    setSelectedDesignationId(designationId);
    setCurrentPage(1);
  };

  const setMonthYear = (value: string) => {
    if (viewMode === "view") {
      setViewMonthYear(value);
    } else {
      setAddMonthYear(value);
    }
  };

  return {
    viewMode,
    loading,
    kpiData,
    totalResults,
    currentPage,
    pageSize,
    monthYear,
    viewMonthYear,
    addMonthYear,
    designationList,
    selectedDesignationId,
    employeeList,
    handleTabChange,
    onShowSizeChange,
    handlePageChange,
    setMonthYear,
    fetchEmployeeList,
    fetchKpiData,
    handleDesignationChange,
    orgId,
    reportingManagerId,
    setViewMode,
  };
};
